<template>
  <div>
    <b-badge
      v-if="
        !isPendingProduct &&
          (subscription.subscriptionStatus === subscriptionDetailPendingCancel ||
            subscription.subscriptionStatus === subscriptionDetailPendingProductChange)
      "
      variant="light"
      class="current-plan-badge mb-2 border-primary text-primary"
      >{{ $t('xCurrentPlan') }}</b-badge
    >
    <b-badge v-else-if="isPendingProduct" variant="primary" class="mb-2 mt-3 pending-change-badge">{{
      $t('xPendingChange')
    }}</b-badge>

    <div
      v-if="isPendingProduct && subscription.subscriptionStatus === subscriptionDetailPendingCancel && hasApparatusesNotOnDealerManagedPlan()"
      class="row mb-4"
    >
      <div class="col-auto">
        <h5 class="mb-0 pb-0">
          <span data-test-hook="pending-product-name"> {{ $t('xMobileLinkBasic') }} </span>
        </h5>
      </div>
    </div>
    <div v-else-if="!isPendingProduct" class="row mb-4">
      <div class="col-auto">
        <h5 class="mb-0 pb-0">
          <span data-test-hook="product-name"> {{ subscription.subscriptionProductName }} </span>
        </h5>
      </div>
    </div>

    <div v-if="subscription.subscriptionStatus === subscriptionDetailPendingCancel">
      <div class="row mb-2">
        <div class="col">
          <span data-test-hook="billing-date-header-text">
            {{ $t('xPlanStatus') }}
          </span>
        </div>
      </div>
      <div v-if="!isPendingProduct" class="row mb-4">
        <div class="col">
          <strong data-test-hook="billing-date">
            {{ $t('xPendingCancellation') }}
          </strong>
        </div>
      </div>
      <div v-else class="row mb-4">
        <div class="col">
          <strong data-test-hook="billing-date">
            {{ $t('xNoPlan') }}
          </strong>
        </div>
      </div>
    </div>
    <div v-if="subscription.subscriptionStatus === subscriptionDetailPendingProductChange">
      <div class="row mb-2">
        <div class="col">
          <span data-test-hook="billing-date-header-text">
            {{ $t('xPlanStatus') }}
          </span>
        </div>
      </div>
      <div v-if="!isPendingProduct">
        <div v-if="isSameProductDifferentCadence" class="row mb-4">
          <div class="col">
            <strong data-test-hook="billing-date">
              {{ $t('xPendingChanges') }}
            </strong>
          </div>
        </div>
        <div v-else class="row mb-4">
          <div class="col">
            <strong data-test-hook="billing-date">
              {{ $t('xPendingDowngrade') }}
            </strong>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="row mb-4">
          <div class="col">
            <strong>{{ $t('xFuturePlan') }}</strong>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!(isPendingProduct && subscription.subscriptionStatus === subscriptionDetailPendingCancel)">
      <div class="row mb-2">
        <div class="col">
          <span data-test-hook="plan-cost-header-text">
            {{ $t('xPlanCost') }}
          </span>
        </div>
      </div>
      <div class="row mb-4">
        <div class="col">
          <strong data-test-hook="plan-cost">
            {{
              '$' +
                subscription.subscriptionCost +
                ' ' +
                displayPlanCostCycle(subscription.subscriptionDurationInMonths)
            }}
          </strong>
        </div>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col">
        <span
          v-if="
            !isPendingProduct &&
              (subscription.subscriptionStatus === subscriptionDetailPendingCancel ||
                subscription.subscriptionStatus === subscriptionDetailPendingProductChange)
          "
          data-test-hook="billing-date-header-text"
        >
          {{ $t('xPlanEndDate') }}
        </span>
        <span
          v-else-if="isPendingProduct && subscription.subscriptionStatus === subscriptionDetailPendingCancel"
          data-test-hook="billing-date-header-text"
        >
          {{ $t('xChangeEffectiveDate') }}
        </span>
        <span
          v-else-if="isPendingProduct && subscription.subscriptionStatus === subscriptionDetailPendingProductChange"
          data-test-hook="billing-date-header-text"
        >
          {{ $t('xPlanStartDate') }}
        </span>
        <span v-else data-test-hook="billing-date-header-text">
          {{ $t('xNextBillingDate') }}
        </span>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <strong data-test-hook="billing-date">
          {{ $moment(subscription.subscriptionRenewalDate).format('MM/DD/YYYY') }}
        </strong>
      </div>
    </div>

    <div v-if="
      isPendingProduct && 
      subscription.subscriptionStatus === subscriptionDetailPendingCancel" 
      class="row">
      <div class="col">
        <div
          v-if="
            (isHomePremiumSubscription(subscription.subscriptionProductId) ||
              isSingleGenSubscription(subscription.subscriptionProductId)) &&
              hasApparatusesNotOnDealerManagedPlan()
          "
          class="mt-4"
        >
          <div>
            <p data-test-hook="display-basic-warning-messages">
              {{ $t('xGeneratorsNotInPlanWarningText') }}
              <button type="button" class="btn btn-icon-button" @click="openModal('generator-features-modal')">
                <i class="far fa-info-circle fa-lg" />
              </button>
            </p>
          </div>
          <GeneratorFeaturesModal />
        </div>
        <div
          v-if="
            (isHomePremiumSubscription(subscription.subscriptionProductId) ||
              isSingleFMSubscription(subscription.subscriptionProductId)) &&
              hasApparatusesNotOnDealerManagedPlan()
          "
          class="mt-2"
        >
          <div>
            <p data-test-hook="display-basic-warning-messages">
              {{ $t('xFuelMonitorsNotInPlanWarningText') }}
              <button type="button" class="btn btn-icon-button" @click="openModal('fuel-monitor-features-modal')">
                <i class="far fa-info-circle fa-lg" />
              </button>
            </p>
          </div>
          <FuelMonitorFeaturesModal />
        </div>
      </div>
    </div>
    <div v-else class="accordion mt-4" role="tablist" style="width:100%;">
      <div class="accordion-wrapper" style="border: 1px solid lightgrey; padding: 10px; border-radius: 10px;">
        <div v-b-toggle="'collapse-premiumPlan-product-pending-' + isPendingProduct">
          <div class="row align-items-center no-gutters">
            <div class="col align-items-center">
              <i class="fa-regular fa-circle-info pr-2"></i><strong>{{ $t('xProductsAndFeatures') }}</strong>
            </div>
            <div class="col-auto">
              <i class="fas fa-chevron-down when-closed"></i>
              <i class="fas fa-chevron-up when-open"></i>
            </div>
          </div>
        </div>
        <b-collapse :id="'collapse-premiumPlan-product-pending-' + isPendingProduct">
          <div class="row">
            <div class="col">
              <hr class="mx-0 px-0" />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col">
              <p class="mb-0 pb-0">
                <strong data-test-hook="products-included-text">
                  {{ $t('xProductsIncludedInPlan') }}
                  {{ subscription.subscriptionProductName }}
                </strong>
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-0">
              <ul id="productsCoveredSingleHomePremium" style="list-style-type: none; padding-inline-start: 13px;">
                <li v-for="(apparatus, index) in subscription.apparatuses" :key="index" class="mb-1">
                  <i :class="getApparatusIcon(apparatus.type)"></i>&nbsp;&nbsp;{{ apparatus.name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="row mt-0 mb-2">
            <div class="col">
              <p class="mb-0 pb-0">
                <strong data-test-hook="features-included-text">
                  {{ $t('xFeaturesIncludedInPlan') }}
                  {{ subscription.subscriptionProductName }}
                </strong>
              </p>
            </div>
          </div>
          <div
            v-if="
              isHomePremiumSubscription(subscription.subscriptionProductId) ||
                isSingleGenSubscription(subscription.subscriptionProductId)
            "
          >
            <div class="row mb-3">
              <div class="col-0 pl-3">
                <i class="fa-kit fa-fw fa-generator"></i>
              </div>
              <div class="col-11 pl-1">
                <span class="pl-0" data-test-hook="features-included-list">
                  {{ $t('xFeaturesInDMHsb1') }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="
              isHomePremiumSubscription(subscription.subscriptionProductId) ||
                isSingleFMSubscription(subscription.subscriptionProductId)
            "
          >
            <div class="row mb-3">
              <div class="col icon-and-text">
                <i class="fa-kit fa-fw fa-tank"></i>
                <span data-test-hook="features-included-monitor-list">
                  {{ $t('xSubFeaturesFmFuelLevelHistoryWarningsBattery') }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="isSingleGenSubscription(subscription.subscriptionProductId)">
            <div class="row">
              <div class="col icon-and-text">
                <i class="far fa-fw fa-circle-check"></i>
                <span data-test-hook="features-included-monitor-list">
                  {{ $t('xFeaturePremiumMonitoringForOneHsb') }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="isSingleFMSubscription(subscription.subscriptionProductId)">
            <div class="row mb-3">
              <div class="col icon-and-text">
                <i class="far fa-fw fa-circle-check"></i>
                <span data-test-hook="features-included-monitor-list">
                  {{ $t('xSubFeaturesFmMonitorOneFm') }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="isSingleHomeSubscription(subscription.subscriptionProductId)">
            <div class="row">
              <div class="col icon-and-text">
                <i class="far fa-fw fa-circle-check"></i>
                <span data-test-hook="features-included-monitor-list">
                  {{ $t('xFeatureSingleHomePremiumSubscription3') }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="isMultiHomeSubscription(subscription.subscriptionProductId)">
            <div class="row">
              <div class="col icon-and-text">
                <i class="far fa-fw fa-circle-check"></i>
                <span data-test-hook="features-included-monitor-list">
                  {{ $t('xSubFeaturesMultiHome2Hsb4FM') }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="
              apparatusesNeedsAttention !== undefined &&
                apparatusesNeedsAttention !== null &&
                apparatusesNeedsAttention.length > 0
            "
          >
            <div class="row mt-3 mb-2">
              <div class="col">
                <p class="mb-0 pb-0">
                  <strong data-test-hook="features-included-text">
                    {{ $t('xProductsNotCoveredBy') }}
                    {{ subscription.subscriptionProductName }}
                  </strong>
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-0">
                <ul id="productsNotCovered" style="list-style-type: none; padding-inline-start: 13px;">
                  <li v-for="(apparatus, index) in apparatusesNeedsAttention" :key="index" class="mb-1">
                    <i :class="getApparatusIcon(apparatus.type)"></i>&nbsp;{{ apparatus.apparatusName }}
                  </li>
                </ul>
              </div>
            </div>
            <div
              v-if="
                apparatusesNeedsAttention !== undefined &&
                  apparatusesNeedsAttention !== null &&
                  apparatusesNeedsAttention?.filter(apparatus => apparatus?.type !== subscription?.apparatuses[0]?.type)
                    .length > 0
              "
            >
              <div class="row mt-0 mb-2">
                <div class="col">
                  <p class="mb-0 pb-0">
                    <strong data-test-hook="features-included-text">
                      {{ $t('xFeaturesNotSupportedInPlan') }}
                      {{ subscription.subscriptionProductName }}
                    </strong>
                  </p>
                </div>
              </div>
              <div
                v-if="
                  apparatusesNeedsAttention !== undefined &&
                    apparatusesNeedsAttention !== null &&
                    apparatusesNeedsAttention.filter(apparatus => apparatus.type === fuelTankApparatusType).length > 0
                "
                class="row"
              >
                <div class="col">
                  <span data-test-hook="features-included-list">
                    <i :class="getApparatusIcon(fuelTankApparatusType)"></i>
                    {{ $t('xFeatureNotIncludedFeaturesFuelTank') }}
                  </span>
                </div>
              </div>
              <div
                v-if="
                  apparatusesNeedsAttention !== undefined &&
                    apparatusesNeedsAttention !== null &&
                    apparatusesNeedsAttention.filter(apparatus => apparatus.type === hsbApparatusType).length > 0
                "
                class="row"
              >
                <div class="col">
                  <span data-test-hook="features-included-list">
                    <i :class="getApparatusIcon(hsbApparatusType)"></i>
                    {{ $t('xFeatureNotIncludedFeaturesHSB') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
      </div>
      <div v-if="!isPendingProduct">
        <div class="mt-4" v-if="subscription.subscriptionStore == subscriptionStoreTypesApple">
          <div class="row">
            <div class="col">
              <span data-test-hook="manage-plan-text">
                {{ $t('xManagePlanOnApple') }}
              </span>
            </div>
          </div>
          <div class="row mb-3">
            <a href="https://account.apple.com/account/manage/section/subscriptions" target="_blank">
              <span data-test-hook="manage-plan-text" class="ml-3">
                {{ 'https://account.apple.com/account/manage/section/subscriptions.' }}
              </span>
            </a>
          </div>
        </div>
        <div class="mt-4" v-else-if="subscription.subscriptionStore == subscriptionStoreTypesGoogle">
          <div class="row">
            <div class="col">
              <span data-test-hook="manage-plan-text">
                {{ $t('xManagePlanOnGoogle') }}
              </span>
            </div>
          </div>
          <div class="row mb-3">
            <a href="https://play.google.com/store/account/subscriptions" target="_blank">
              <span data-test-hook="manage-plan-text" class="ml-3">
                {{ 'https://play.google.com/store/account/subscriptions' }}
              </span>
            </a>
          </div>
        </div>
        <div
          v-if="
            isSingleApparatusSubscription(premiumSubscriptionApparatusMultiplePlans?.subscriptionProductId) ||
              (premiumSubscriptionApparatusMultiplePlans?.apparatuses.length === 1 &&
                subscription.subscriptionStore === subscriptionStoreTypesWeb)
          "
          class="mt-4"
        >
          <button
            type="button"
            class="btn btn-warning"
            @click="$emit('openCancelPlanMultiplePlansModal')"
            :title="$t('xCancelPlanLowercase')"
            data-test-hook="CancelMultiplePlansBtn"
          >
            {{ $t('xCancelPlanLowercase') }}
          </button>
        </div>
        <div
          v-else-if="
            subscription.subscriptionStatus !== subscriptionDetailPendingCancel &&
              subscription.subscriptionStatus !== subscriptionDetailPendingProductChange &&
              subscription.subscriptionStore === subscriptionStoreTypesWeb
          "
          class="mt-4"
        >
          <button
            type="button"
            class="btn btn-warning"
            @click="openModal('manage-plan-modal')"
            :title="$t('xManageYourPlanBtn')"
            data-test-hook="ManageYourPlanBtn"
            :disabled="this.subscriptionCancelInitiated"
          >
            <i class="fas fa-pencil" />&nbsp;{{ $t('xManageYourPlanBtn') }}
          </button>
        </div>
        <div v-if="!isSameProductDifferentCadence && subscription.subscriptionStatus !== subscriptionDetailPendingCancel" class="row mt-4">
          <div class="col">
            <p data-test-hook="AutoRenewMsg" class="auto-renew-message text-small">
              {{ $t('xThisSubscriptionWillAutoRenewShortVersion') }}
            </p>
          </div>
        </div>
        <div
          v-if="
            subscription.subscriptionStatus === subscriptionDetailPendingProductChange &&
              subscription.subscriptionStore === subscriptionStoreTypesWeb
          "
          class="mt-4 pb-3"
        >
          <button
            type="button"
            class="btn btn-warning"
            @click="openModal('manage-plan-modal')"
            :title="$t('xManageYourPlanBtn')"
            data-test-hook="ManageYourPlanBtn"
            :disabled="this.subscriptionCancelInitiated"
          >
            <i class="fas fa-pencil" />&nbsp;{{ $t('xManageYourPlanBtn') }}
          </button>
        </div>
      </div>
      <div v-else>
        <div v-if="subscription.subscriptionStatus === subscriptionDetailPendingProductChange" class="row mt-4">
          <div class="col">
            <p data-test-hook="AutoRenewMsg" class="auto-renew-message text-small">
              {{ $t('xThisSubscriptionWillAutoRenewShortVersion') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import * as Constants from '@/constants'
import GeneratorFeaturesModal from './GeneratorFeaturesModal.vue'
import FuelMonitorFeaturesModal from './FuelMonitorFeaturesModal.vue'

const productIdentifier = {
  SINGLE_FM: 'p-fm-1',
  SINGLE_HSB: 'p-hsb-1',
  SINGLE_HOME: 'p-home-1',
  MULTI_HOME: 'p-home-2'
}

export default {
  components: {
    GeneratorFeaturesModal,
    FuelMonitorFeaturesModal
  },
  props: [
    'subscription',
    'isPendingProduct',
    'isSameProductDifferentCadence',
    'apparatusesNeedsAttention',
    'premiumSubscriptionApparatusMultiplePlans',
  ],
  computed: {
    ...mapState({ 
      subscriptionCancelInitiated: state => state.subscriptions.subscriptionCancelInitiated,
      apparatuses: state => state.apparatus.apparatuses
    }),
    subscriptionDetailPendingCancel() {
      return Constants.SubscriptionDetailPendingCancel
    },
    subscriptionDetailPendingProductChange() {
      return Constants.SubscriptionDetailPendingProductChange
    },
    subscriptionStoreTypesApple() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Apple')
    },
    subscriptionStoreTypesGoogle() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Google')
    },
    subscriptionStoreTypesWeb() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Web')
    },
    hsbApparatusType() {
      return Constants.HsbApparatusType
    },
    fuelTankApparatusType() {
      return Constants.FuelTankApparatusType
    }
  },
  methods: {
    displayPlanCostCycle(duration) {
      if (duration == 1) return this.$t('xPerMonth')
      if (duration == 12) return this.$t('xPerYear')
    },
    getApparatusIcon(type) {
      switch (type) {
        case Constants.HsbApparatusType:
          return 'fa-kit fa-fw fa-generator'
        case Constants.FuelTankApparatusType:
          return 'fa-kit fa-fw fa-tank'
        default:
          return 'fa-kit fa-fw fa-generator'
      }
    }, 
    /** Returns true if there is a non Dealer Managed apparatus on the subscription */
    hasApparatusesNotOnDealerManagedPlan() {
      return this.apparatuses?.some(
        x => !x.isDealerManaged && this.subscription.apparatuses.filter(
          apparatus => apparatus.id === x.apparatusId)
        )
    }
  }
}
</script>
<style scoped lang="scss">
.current-plan-badge {
  border: 2px solid;
  padding: 5px;
}

.collapsed .when-open,
.not-collapsed .when-closed {
  display: none;
}
</style>
